import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import ToolTip from 'design-system/ToolTip';
import { CardView } from 'design-system/CardView';
import BlurAmountView from 'design-system/BlurAmountView';
import useColors from 'hooks/useColors';
import { selectIsScrambled } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import Links from 'utils/links';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { setTransactionsRequesting } from 'actions';
import BudgetingCircleChart from 'features/feed/components/analytics/BudgetingCircleChart';
import BudgetingGraphLabel from './BudgetingGraphLabel';
import { BudgetRollingAmountLabel } from './BudgetRollingAmountLabel';
import { getFontSize } from '../utils';
const strings = {
    amountToolTipTitle: 'How is it estimated?',
    amountToolTipSubTitle: `This is what’s left from your total budget after subtracting both your committed and actual spending. This number is not correlated to your bank account balance.`,
};
// Arcs shorter than this don't render cleanly on the ring.
const MIN_RATIO_FOR_RENDER = 0.01;
// Lengthening a ratio never takes it past 1 (a full circle): from 1 on the chart
// draws a closed ring, with no rounded start or end.
const lengthenUpToFullCircle = (ratio, minimum) => Math.min(Math.max(ratio, minimum), Math.max(ratio, 1));
const getRenderableRatios = (committedRatio, spentRatio) => {
    const renderedSpentRatio = spentRatio > 0
        ? lengthenUpToFullCircle(spentRatio, MIN_RATIO_FOR_RENDER)
        : spentRatio;
    const renderedCommittedRatio = committedRatio != null
        ? lengthenUpToFullCircle(committedRatio, renderedSpentRatio + MIN_RATIO_FOR_RENDER)
        : committedRatio;
    return {
        committedRatio: renderedCommittedRatio,
        spentRatio: renderedSpentRatio,
    };
};
const BudgetingCarouselItem = ({ item, showFullData }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const committedBudgetRatio = (item.hasCircle || showFullData) &&
        'committed' in item &&
        item.committed &&
        item.totalBudget
        ? (item.committed + item.spending) / item.totalBudget
        : null;
    const spentBudgetRatio = item.totalBudget
        ? item.spending / item.totalBudget
        : 0;
    const totalSpending = ((item.hasCircle || showFullData) && 'committed' in item && item.committed
        ? item.committed
        : 0) + item.spending;
    const resultantBudget = (item.baseLimit || 0) + (item.rollingAccumulatedLimit || 0) - totalSpending;
    const scrambled = useAppSelector(selectIsScrambled);
    const leftOfBudget = (item.totalBudget || 0) - totalSpending;
    const isToolTipStyle = useMemo(() => leftOfBudget.toFixed(0).length < 10, [leftOfBudget]);
    const navigation = useMainStackNavigation();
    const onPressSpending = () => {
        dispatch(setTransactionsRequesting);
        navigation.navigateDeprecated('TransactionsList', {
            from: item.from,
            to: item.to,
        });
    };
    const onPressCommittedSpending = () => {
        navigation.navigateDeprecated('CommittedList', {
            fromDate: item.from,
            toDate: item.to,
        });
    };
    const onPressLearnMore = useCallback(() => {
        Links.openKnownLink('balanceAndBudget');
    }, []);
    return (<CardView style={styles.card} withPadding webPadding>
      <BudgetRollingAmountLabel showFullData={showFullData} rollingAccumulatedLimit={item.rollingAccumulatedLimit} currency={item.currency} defaultBudget={(item.totalBudget || 0) - (item.rollingAccumulatedLimit || 0)} isLatest={'daysLeft' in item && !!item.daysLeft}/>
      <View style={styles.graphContainer}>
        <BudgetingCircleChart large isEmpty={!!item.rollingAccumulatedLimit &&
            item.totalLimit != null &&
            item.totalLimit <= 0} {...getRenderableRatios(committedBudgetRatio, spentBudgetRatio)}/>
        {!!item.rollingAccumulatedLimit &&
            !!item.totalLimit &&
            item.baseLimit != null &&
            item.totalLimit < 0 ? (<View style={styles.graphText}>
            <View style={styles.row}>
              <Amount {...getFontSize(resultantBudget)} value={{
                value: resultantBudget,
                currency: item.currency,
            }} centered showFraction numberOfLines={1} numbersBold/>
            </View>
            <BlurAmountView enabled={scrambled}>
              <Text TextThin-14 Gray centered adjustsFontSizeToFit>
                {`will roll over`}
              </Text>
            </BlurAmountView>
          </View>) : (<View style={styles.graphText}>
            <View style={[
                styles.row,
                leftOfBudget < 0 && styles.chartTextExtraMargin,
            ]}>
              <View style={styles.flex}/>
              <Amount {...getFontSize(Math.abs(leftOfBudget))} value={{
                value: Math.abs(leftOfBudget),
                currency: item.currency,
            }} centered showFraction numberOfLines={1} numbersBold/>
              <View style={isToolTipStyle ? styles.tooltipContainer : null}>
                <ToolTip colorKey="secondary" title={strings.amountToolTipTitle} subtitle={strings.amountToolTipSubTitle} small learnMore onPressLearnMore={onPressLearnMore}/>
              </View>
            </View>
            <BlurAmountView enabled={scrambled}>
              <Text TextThin-14 Gray centered adjustsFontSizeToFit>
                {leftOfBudget >= 0 ? 'left of' : 'over your budget\nof'}{' '}
                <Text NumbersThin-14 Gray centered adjustsFontSizeToFit>
                  {formatAmount(item.totalBudget, item.currency, 2)}
                </Text>
              </Text>
            </BlurAmountView>
          </View>)}
      </View>
      <Spacer.H24 />
      <BudgetingGraphLabel title="Spending" color={colors.gradients.ultimate.colors[0]} amount={{ amount: item.spending, currency: item.currency }} onPress={showFullData ? undefined : onPressSpending}/>
      {item.hasCircle || showFullData ? (<BudgetingGraphLabel title="Committed spending" color={colors.gradients.darkPink.colors[0]} amount={{
                amount: (item.hasCircle || showFullData) &&
                    'committed' in item &&
                    item?.committed
                    ? item.committed
                    : 0,
                currency: item.currency,
            }} onPress={showFullData ? undefined : onPressCommittedSpending}/>) : null}
      <Spacer.W8 />
    </CardView>);
};
const styles = StyleSheet.create({
    card: {
        marginBottom: rem(16),
    },
    graphContainer: {
        justifyContent: 'center',
        alignSelf: 'center',
        paddingVertical: rem(16),
    },
    graphText: {
        position: 'absolute',
        alignSelf: 'center',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: rem(140),
    },
    row: {
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    tooltipContainer: {
        flex: 1,
        flexDirection: 'row',
    },
    chartTextExtraMargin: {
        marginTop: rem(8),
    },
});
export default BudgetingCarouselItem;
